import React, { useMemo, useState } from 'react';
import AnalyticsSettingsPanel, { useAnalyticsSettings, SettingsButton } from '../common/AnalyticsSettings';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const AVAILABLE_METRICS = [
  { id: 'totalRecords', label: 'Total Records', icon: '📋', defaultVisible: true },
  { id: 'uniqueCategories', label: 'Categories', icon: '📂', defaultVisible: true },
  { id: 'uniqueCountries', label: 'Countries', icon: '🌍', defaultVisible: true },
  { id: 'uniqueMills', label: 'Mills', icon: '🏭', defaultVisible: true },
];

const AVAILABLE_CHARTS = [
  { id: 'byCategory', label: 'By Category', icon: '📂', defaultVisible: true, defaultType: 'bar' },
  { id: 'byCountry', label: 'By Country', icon: '🌍', defaultVisible: true, defaultType: 'bar' },
  { id: 'byMill', label: 'By Mill', icon: '🏭', defaultVisible: true, defaultType: 'bar' },
  { id: 'tariffDistribution', label: 'Tariff Distribution', icon: '💵', defaultVisible: true, defaultType: 'bar' },
];

/**
 * TariffAnalyticsDashboard - Analytics Dashboard for Tariff Log
 * Displays cost trends, tariff categories, and pricing analysis
 */
const TariffAnalyticsDashboard = ({ data, onClose }) => {
  const [selectedView, setSelectedView] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState } = usePresence('tariffAnalytics', 'global', true);
  
  const {
    settings,
    updateSetting,
    toggleMetric,
    toggleChart,
    setChartType,
    resetToDefaults,
    isMetricVisible,
    isChartVisible,
  } = useAnalyticsSettings('tariff_log', AVAILABLE_METRICS, AVAILABLE_CHARTS);

  const analytics = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalRecords: 0,
        uniqueCategories: 0,
        uniqueCountries: 0,
        uniqueMills: 0,
        avgAD: 0,
        avgCV: 0,
        avgSec232: 0,
        avgSec301: 0,
        avgReciprocal: 0,
        byCategory: [],
        byCountry: [],
        byMill: [],
        tariffDistribution: [],
        highTariffItems: [],
      };
    }

    const totalRecords = data.length;
    const uniqueCategories = new Set(data.map(d => d.material_category).filter(Boolean)).size;
    const uniqueCountries = new Set(data.map(d => d.country).filter(Boolean)).size;
    const uniqueMills = new Set(data.map(d => d.mill).filter(Boolean)).size;

    // Average tariff rates
    const validAD = data.filter(d => d.ad_percent_nov_13 != null && !isNaN(parseFloat(d.ad_percent_nov_13)));
    const validCV = data.filter(d => d.cv_percent_nov_13 != null && !isNaN(parseFloat(d.cv_percent_nov_13)));
    const valid232 = data.filter(d => d.section_232_percent_nov_13 != null && !isNaN(parseFloat(d.section_232_percent_nov_13)));
    const valid301 = data.filter(d => d.section_301_percent_nov_13 != null && !isNaN(parseFloat(d.section_301_percent_nov_13)));
    const validRecip = data.filter(d => d.reciprocal_tariffs_percent_nov_13 != null && !isNaN(parseFloat(d.reciprocal_tariffs_percent_nov_13)));

    const avgAD = validAD.length > 0 ? validAD.reduce((s, d) => s + parseFloat(d.ad_percent_nov_13), 0) / validAD.length : 0;
    const avgCV = validCV.length > 0 ? validCV.reduce((s, d) => s + parseFloat(d.cv_percent_nov_13), 0) / validCV.length : 0;
    const avgSec232 = valid232.length > 0 ? valid232.reduce((s, d) => s + parseFloat(d.section_232_percent_nov_13), 0) / valid232.length : 0;
    const avgSec301 = valid301.length > 0 ? valid301.reduce((s, d) => s + parseFloat(d.section_301_percent_nov_13), 0) / valid301.length : 0;
    const avgReciprocal = validRecip.length > 0 ? validRecip.reduce((s, d) => s + parseFloat(d.reciprocal_tariffs_percent_nov_13), 0) / validRecip.length : 0;

    // By Category
    const categoryMap = {};
    data.forEach(d => {
      const cat = d.material_category || 'Unspecified';
      if (!categoryMap[cat]) {
        categoryMap[cat] = { name: cat, count: 0, avgAD: 0, avgCV: 0, total: 0 };
      }
      categoryMap[cat].count++;
      categoryMap[cat].avgAD += parseFloat(d.ad_percent_nov_13) || 0;
      categoryMap[cat].avgCV += parseFloat(d.cv_percent_nov_13) || 0;
    });
    const byCategory = Object.values(categoryMap).map(c => ({
      ...c,
      avgAD: c.count > 0 ? c.avgAD / c.count : 0,
      avgCV: c.count > 0 ? c.avgCV / c.count : 0,
    })).sort((a, b) => b.count - a.count);

    // By Country
    const countryMap = {};
    data.forEach(d => {
      const country = d.country || 'Unspecified';
      if (!countryMap[country]) {
        countryMap[country] = { name: country, count: 0, avgAD: 0, avgCV: 0, avg232: 0, avg301: 0 };
      }
      countryMap[country].count++;
      countryMap[country].avgAD += parseFloat(d.ad_percent_nov_13) || 0;
      countryMap[country].avgCV += parseFloat(d.cv_percent_nov_13) || 0;
      countryMap[country].avg232 += parseFloat(d.section_232_percent_nov_13) || 0;
      countryMap[country].avg301 += parseFloat(d.section_301_percent_nov_13) || 0;
    });
    const byCountry = Object.values(countryMap).map(c => ({
      ...c,
      avgAD: c.count > 0 ? c.avgAD / c.count : 0,
      avgCV: c.count > 0 ? c.avgCV / c.count : 0,
      avg232: c.count > 0 ? c.avg232 / c.count : 0,
      avg301: c.count > 0 ? c.avg301 / c.count : 0,
      totalTariff: (c.count > 0 ? c.avgAD / c.count : 0) + (c.count > 0 ? c.avgCV / c.count : 0) + (c.count > 0 ? c.avg232 / c.count : 0) + (c.count > 0 ? c.avg301 / c.count : 0),
    })).sort((a, b) => b.count - a.count);

    // By Mill
    const millMap = {};
    data.forEach(d => {
      const mill = d.mill || 'Unspecified';
      if (!millMap[mill]) {
        millMap[mill] = { name: mill, count: 0 };
      }
      millMap[mill].count++;
    });
    const byMill = Object.values(millMap).sort((a, b) => b.count - a.count).slice(0, 15);

    // Tariff distribution
    const tariffDistribution = [
      { name: 'AD Tariffs', value: validAD.length, avg: avgAD },
      { name: 'CV Tariffs', value: validCV.length, avg: avgCV },
      { name: 'Section 232', value: valid232.length, avg: avgSec232 },
      { name: 'Section 301', value: valid301.length, avg: avgSec301 },
      { name: 'Reciprocal', value: validRecip.length, avg: avgReciprocal },
    ];

    // High tariff items (total > 25%)
    const highTariffItems = data.map(d => {
      const total = (parseFloat(d.ad_percent_nov_13) || 0) + 
                   (parseFloat(d.cv_percent_nov_13) || 0) + 
                   (parseFloat(d.section_232_percent_nov_13) || 0) + 
                   (parseFloat(d.section_301_percent_nov_13) || 0) +
                   (parseFloat(d.reciprocal_tariffs_percent_nov_13) || 0);
      return { ...d, totalTariff: total };
    }).filter(d => d.totalTariff > 25).sort((a, b) => b.totalTariff - a.totalTariff).slice(0, 20);

    return {
      totalRecords,
      uniqueCategories,
      uniqueCountries,
      uniqueMills,
      avgAD,
      avgCV,
      avgSec232,
      avgSec301,
      avgReciprocal,
      byCategory,
      byCountry,
      byMill,
      tariffDistribution,
      highTariffItems,
    };
  }, [data]);

  const colors = {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    dark: '#1e293b',
    chartColors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'],
  };

  const formatNumber = (num, decimals = 2) => {
    return num.toFixed(decimals);
  };

  const MetricCard = ({ title, value, subtitle, icon, color }) => (
    <div style={{
      backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>{title}</span>
        <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      </div>
      <div style={{ fontSize: '1.75rem', fontWeight: '700', color: colors.dark, margin: '0.5rem 0' }}>{value}</div>
      {subtitle && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{subtitle}</span>}
    </div>
  );

  const BarChart = ({ data, valueKey, labelKey, title, maxBars = 10, suffix = '' }) => {
    const chartData = data.slice(0, maxBars);
    const maxValue = Math.max(...chartData.map(d => d[valueKey] || 0), 1);

    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {chartData.map((item, idx) => {
            const value = item[valueKey] || 0;
            const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '100px', fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item[labelKey]}>
                  {item[labelKey]}
                </div>
                <div style={{ flex: 1, height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${percentage}%`, height: '100%',
                    backgroundColor: colors.chartColors[idx % colors.chartColors.length],
                    borderRadius: '4px', transition: 'width 0.5s ease',
                  }} />
                </div>
                <div style={{ width: '70px', fontSize: '0.75rem', color: '#64748b', textAlign: 'right' }}>
                  {typeof value === 'number' ? formatNumber(value) : value}{suffix}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const TariffRateCard = ({ title, rate, color, items }) => (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', textAlign: 'center' }}>
      <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem' }}>{title}</div>
      <div style={{ fontSize: '2rem', fontWeight: '700', color }}>{formatNumber(rate)}%</div>
      <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.25rem' }}>{items} items</div>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'countries', label: 'By Country', icon: '🌍' },
    { id: 'categories', label: 'Categories', icon: '📋' },
  ];

  const renderOverview = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard title="Total Records" value={analytics.totalRecords} subtitle={`${analytics.uniqueCategories} categories`} icon="📋" color={colors.primary} />
        <MetricCard title="Countries" value={analytics.uniqueCountries} subtitle="Origins" icon="🌍" color={colors.info} />
        <MetricCard title="Mills" value={analytics.uniqueMills} subtitle="Suppliers" icon="🏭" color={colors.success} />
        <MetricCard title="High Tariff Items" value={analytics.highTariffItems.length} subtitle=">25% total" icon="⚠️" color={colors.danger} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <TariffRateCard title="Avg AD %" rate={analytics.avgAD} color={colors.primary} items={analytics.tariffDistribution[0]?.value || 0} />
        <TariffRateCard title="Avg CV %" rate={analytics.avgCV} color={colors.info} items={analytics.tariffDistribution[1]?.value || 0} />
        <TariffRateCard title="Avg Sec 232" rate={analytics.avgSec232} color={colors.success} items={analytics.tariffDistribution[2]?.value || 0} />
        <TariffRateCard title="Avg Sec 301" rate={analytics.avgSec301} color={colors.warning} items={analytics.tariffDistribution[3]?.value || 0} />
        <TariffRateCard title="Avg Reciprocal" rate={analytics.avgReciprocal} color={colors.danger} items={analytics.tariffDistribution[4]?.value || 0} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        <BarChart data={analytics.byCategory} valueKey="count" labelKey="name" title="Items by Category" />
        <BarChart data={analytics.byCountry} valueKey="count" labelKey="name" title="Items by Country" />
      </div>
    </>
  );

  const renderCountries = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <BarChart data={analytics.byCountry} valueKey="avgAD" labelKey="name" title="Avg AD Rate by Country" suffix="%" />
        <BarChart data={analytics.byCountry} valueKey="avgCV" labelKey="name" title="Avg CV Rate by Country" suffix="%" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        <BarChart data={analytics.byCountry} valueKey="avg232" labelKey="name" title="Avg Section 232 by Country" suffix="%" />
        <BarChart data={analytics.byCountry} valueKey="totalTariff" labelKey="name" title="Total Avg Tariff by Country" suffix="%" />
      </div>
    </>
  );

  const renderCategories = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <BarChart data={analytics.byCategory} valueKey="avgAD" labelKey="name" title="Avg AD Rate by Category" suffix="%" />
        <BarChart data={analytics.byCategory} valueKey="avgCV" labelKey="name" title="Avg CV Rate by Category" suffix="%" />
      </div>
      <BarChart data={analytics.byMill} valueKey="count" labelKey="name" title="Items by Mill" maxBars={15} />
    </>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
      zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem',
    }}>
      <div style={{
        backgroundColor: '#f1f5f9', borderRadius: '16px', width: '100%', maxWidth: '1400px', maxHeight: '95vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      }}>
        <div style={{
          padding: '1.25rem 1.5rem', background: `linear-gradient(135deg, ${colors.warning} 0%, ${colors.danger} 100%)`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>💰 Tariff Analytics</h2>
            <p style={{ margin: '0.25rem 0 0', opacity: 0.9, fontSize: '0.85rem' }}>Tariff rates, categories, and country analysis</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
            <button onClick={onClose} style={{
              padding: '0.5rem 1rem', backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white',
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem',
            }}>✕ Close</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setSelectedView(tab.id)} style={{
              padding: '0.75rem 1.25rem', backgroundColor: selectedView === tab.id ? colors.warning : 'transparent',
              color: selectedView === tab.id ? 'white' : '#64748b', border: 'none', borderRadius: '8px 8px 0 0',
              cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {analytics.totalRecords === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
              <h3 style={{ margin: 0, color: colors.dark }}>No Data Available</h3>
              <p style={{ margin: '0.5rem 0 0' }}>Add tariff data to see analytics</p>
            </div>
          ) : (
            <>
              {selectedView === 'overview' && renderOverview()}
              {selectedView === 'countries' && renderCountries()}
              {selectedView === 'categories' && renderCategories()}
            </>
          )}
        </div>

        <div style={{
          padding: '0.75rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#94a3b8',
        }}>
          <span>{analytics.totalRecords} total records</span>
          <span>{analytics.uniqueCategories} categories | {analytics.uniqueCountries} countries | {analytics.uniqueMills} mills</span>
        </div>
      </div>
    </div>
  );
};

export default TariffAnalyticsDashboard;

